// Rewards page — three-tab layout (Available / In Review / Claimed)
// backed by GET /api/me/offers + GET /api/me/offers/claims.
//
// Available tab — shows currently-visible offers the user qualifies
// for. Each offer card surfaces an action: Claim (auto-credit on tap)
// or Submit-for-review (manual). Already-credited offers (e.g.
// signup_bonus that fired on first login) appear as "Claimed ✓" with
// the credited date.
//
// In Review — pending/approved-but-not-credited claims (the manual
// queue). Claimed — credited history.
//
// Empty states say something useful per tab so the page never reads
// "loading…" then "nothing here". The data layer was verified
// end-to-end in the previous turn — engine credits idempotently and
// the UNIQUE index prevents double-fire.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { api, ApiError, User } from '@baaligames/shared-ui/lib/api';
import { useT, useI18n } from '@baaligames/shared-ui/lib/i18n';
import type { Lang } from '@baaligames/shared-ui/lib/translations';
import { REFERRAL_INTROS } from '@baaligames/shared-ui/lib/referralIntros';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import OfferCard, { OfferLike, ClaimLike } from '@baaligames/shared-ui/components/OfferCard';
import OfferHero, { pickHeroOffer } from '@baaligames/shared-ui/components/OfferHero';
import Icon from '@baaligames/shared-ui/components/Icon';

type Tab = 'available' | 'review' | 'claimed';

type ClaimWithOffer = ClaimLike & {
  offer_id: number;
  offer_title: string;
  offer_type: string;
  user_id: number;
  trigger_kind: string | null;
  trigger_ref: string | null;
};

export default function Rewards() {
  const t = useT();
  const { user } = useAuth();
  const { push } = useToast();
  const [tab, setTab] = useState<Tab>('available');

  // Available tab data
  const [offers, setOffers] = useState<OfferLike[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);

  // Review + Claimed tabs share the same /me/offers/claims endpoint
  // with a state filter; we keep them as two paginated lists.
  const [pendingClaims, setPendingClaims] = useState<ClaimWithOffer[]>([]);
  const [creditedClaims, setCreditedClaims] = useState<ClaimWithOffer[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);

  const [busyOfferId, setBusyOfferId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function loadAll() {
    if (!user) return;
    setErr(null);
    setOffersLoading(true);
    setClaimsLoading(true);
    try {
      const [offersR, pendingR, creditedR] = await Promise.all([
        api.get<{ offers: OfferLike[] }>('/me/offers'),
        api.get<{ rows: ClaimWithOffer[] }>('/me/offers/claims?state=pending&limit=50'),
        api.get<{ rows: ClaimWithOffer[] }>('/me/offers/claims?state=credited&limit=50'),
      ]);
      setOffers(offersR.offers);
      setPendingClaims(pendingR.rows);
      setCreditedClaims(creditedR.rows);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setOffersLoading(false);
      setClaimsLoading(false);
    }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user?.id]);

  async function claim(offerId: number) {
    setBusyOfferId(offerId);
    setErr(null);
    try {
      const r = await api.post<{ result: { state: string; amount?: number } | null }>(
        `/me/offers/${offerId}/claim`
      );
      const result = r.result;
      if (!result) {
        push('info', t('rewards.toast.alreadyClaimed'));
      } else if (result.state === 'credited') {
        push('success', t('rewards.toast.credited', { amount: '₹' + (result.amount ?? 0).toLocaleString('en-IN') }));
        // wallet number on the dashboard / NavBar chip should reflect
        // the credit immediately — refresh the global store.
        await refreshWallet();
      } else if (result.state === 'pending') {
        push('info', t('rewards.toast.submitted'));
      } else if (result.state === 'duplicate') {
        push('info', t('rewards.toast.alreadyClaimed'));
      } else if (result.state === 'rejected') {
        push('error', t('rewards.toast.rejected'));
      }
      await loadAll();
    } catch (e) {
      setErr((e as ApiError).message);
      push('error', (e as ApiError).message);
    } finally {
      setBusyOfferId(null);
    }
  }

  // Available — every offer the engine considered eligible. Already-
  // credited ones still show here but with a "Claimed ✓" badge (per
  // the OfferCard logic) so the player sees what they've earned.
  const reviewCount = pendingClaims.length;
  const claimedCount = creditedClaims.length;

  return (
    <div className="space-y-4">
      {/* Page header */}
      <header className="px-1">
        <h1 className="text-xl font-display font-extrabold uppercase tracking-wider text-arena-800 dark:text-neon-100">
          {t('rewards.title')}
        </h1>
        <p className="text-xs text-arena-500 dark:text-neon-200/60 mt-0.5">
          {t('rewards.subtitle')}
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl
                      bg-white/60 dark:bg-arena-800/60
                      border border-arena-200/60 dark:border-arena-600/40">
        <TabButton active={tab === 'available'} onClick={() => setTab('available')}>
          <Icon name="redeem" size={16} filled /> {t('rewards.tab.available')}
          {offers.length > 0 && <Pill>{offers.length}</Pill>}
        </TabButton>
        <TabButton active={tab === 'review'} onClick={() => setTab('review')}>
          <Icon name="pending" size={16} filled /> {t('rewards.tab.inReview')}
          {reviewCount > 0 && <Pill>{reviewCount}</Pill>}
        </TabButton>
        <TabButton active={tab === 'claimed'} onClick={() => setTab('claimed')}>
          <Icon name="check_circle" size={16} filled /> {t('rewards.tab.claimed')}
          {claimedCount > 0 && <Pill>{claimedCount}</Pill>}
        </TabButton>
      </div>

      {err && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30
                        text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Tab body */}
      {tab === 'available' && (
        <section className="space-y-4">
          {/* "How do I earn extra?" now has ONE home (UX Phase 2): the referral
              card (moved here from Profile) + a cashback note, above the offers. */}
          {user && <ReferralCard user={user} />}
          <CashbackNote />
          {offersLoading && <SkeletonRows />}

          {/* Phase 14 hero — promotes the top offer with artwork.
              Renders only when at least one visible offer has
              image_url; otherwise space simply collapses and the card
              grid below carries the full presentation as before. */}
          {!offersLoading && (() => {
            const hero = pickHeroOffer(offers);
            if (!hero) return null;
            return (
              <OfferHero
                offer={hero}
                eyebrow="Featured Reward"
                onClaim={
                  hero.my_latest_claim?.state === 'credited'
                    || hero.my_latest_claim?.state === 'pending'
                    || hero.my_latest_claim?.state === 'approved'
                    ? undefined
                    : claim
                }
                busy={busyOfferId === hero.id}
              />
            );
          })()}

          {!offersLoading && offers.length === 0 && (
            <EmptyState icon="hourglass_empty"
                        title={t('rewards.empty.available.title')}
                        body={t('rewards.empty.available.body')} />
          )}
          {offers.map((o) => (
            <OfferCard
              key={o.id}
              offer={o}
              onClaim={
                // Suppress the action button on offers already in
                // a non-actionable state so the user can't double-tap.
                o.my_latest_claim?.state === 'credited'
                  || o.my_latest_claim?.state === 'pending'
                  || o.my_latest_claim?.state === 'approved'
                  ? undefined
                  : claim
              }
              busy={busyOfferId === o.id}
            />
          ))}
        </section>
      )}

      {tab === 'review' && (
        <section className="space-y-3">
          {claimsLoading && <SkeletonRows />}
          {!claimsLoading && reviewCount === 0 && (
            <EmptyState icon="inbox"
                        title={t('rewards.empty.review.title')}
                        body={t('rewards.empty.review.body')} />
          )}
          {pendingClaims.map((c) => (
            <ClaimRow key={c.id} c={c} t={t} tone="warning" iconName="pending" />
          ))}
        </section>
      )}

      {tab === 'claimed' && (
        <section className="space-y-3">
          {claimsLoading && <SkeletonRows />}
          {!claimsLoading && claimedCount === 0 && (
            <EmptyState icon="card_giftcard"
                        title={t('rewards.empty.claimed.title')}
                        body={t('rewards.empty.claimed.body')} />
          )}
          {creditedClaims.map((c) => (
            <ClaimRow key={c.id} c={c} t={t} tone="success" iconName="check_circle" />
          ))}
        </section>
      )}
    </div>
  );
}

// ─── Small presentational helpers ───

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs sm:text-sm font-bold rounded-xl transition ${
        active
          ? 'bg-brand-500 text-white shadow'
          : 'text-arena-700 dark:text-neon-200/70 hover:bg-white/40 dark:hover:bg-arena-700/40'
      }`}
    >
      {children}
    </button>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-white/30 text-[10px] font-bold tabular-nums">
      {children}
    </span>
  );
}

function EmptyState({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="rounded-2xl bg-white/40 dark:bg-arena-800/40 border border-arena-200/60 dark:border-arena-600/40 p-6 text-center">
      <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center
                      bg-arena-100 dark:bg-arena-700/60 text-arena-500 dark:text-neon-200/60">
        <Icon name={icon} size={28} />
      </div>
      <h3 className="mt-3 font-display font-extrabold text-base text-arena-800 dark:text-neon-100">{title}</h3>
      <p className="text-xs text-arena-500 dark:text-neon-200/60 mt-1 max-w-sm mx-auto">{body}</p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-2xl border border-arena-200/40 dark:border-arena-600/30
                                bg-white/40 dark:bg-arena-800/30 p-4 animate-pulse">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-arena-200/50 dark:bg-arena-600/40" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/2 rounded bg-arena-200/50 dark:bg-arena-600/40" />
              <div className="h-3 w-3/4 rounded bg-arena-200/30 dark:bg-arena-600/30" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function ClaimRow({
  c, t, tone, iconName,
}: {
  c: ClaimWithOffer;
  t: ReturnType<typeof useT>;
  tone: 'success' | 'warning';
  iconName: string;
}) {
  const tint = tone === 'success'
    ? 'from-emerald-500/15 to-green-500/5 border-emerald-400/30'
    : 'from-amber-500/15 to-orange-500/5 border-amber-400/30';
  const amountTint = tone === 'success' ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300';
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${tint} p-4 flex items-center gap-3`}>
      <div className="shrink-0 w-10 h-10 rounded-xl grid place-items-center bg-white/30 dark:bg-white/5
                      text-arena-700 dark:text-neon-200">
        <Icon name={iconName} size={20} filled />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-arena-800 dark:text-neon-100 truncate">
          {c.offer_title}
        </div>
        <div className="text-xs text-arena-500 dark:text-neon-200/60 truncate">
          {tone === 'success'
            ? t('rewards.row.creditedOn', { date: new Date(c.created_at).toLocaleDateString() })
            : t('rewards.row.submittedOn', { date: new Date(c.created_at).toLocaleDateString() })}
        </div>
      </div>
      <div className={`shrink-0 font-display font-black text-lg tabular-nums ${amountTint}`}>
        ₹{c.computed_amount.toLocaleString('en-IN')}
      </div>
    </div>
  );
}

// ─── Cashback note ─────────────────────────────────────────────────
// A small explainer so "how do I earn extra?" is fully answered here:
// offers above, referral below, and automatic cashback (which lands in
// the wallet) summarised with a link to see it.
function CashbackNote() {
  return (
    <Link
      to="/wallet"
      className="card p-3 flex items-center gap-3 active:scale-[.99] transition"
    >
      <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0
                      bg-gradient-to-br from-emerald-300/40 to-teal-300/30
                      dark:from-emerald-400/20 dark:to-teal-500/20
                      text-emerald-700 dark:text-emerald-300">
        <Icon name="savings" size={20} filled />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-arena-800 dark:text-neon-100">Automatic cashback</div>
        <div className="text-xs text-arena-500 dark:text-neon-200/60">
          You earn cashback on your play automatically — it lands in your wallet.
        </div>
      </div>
      <Icon name="chevron_right" size={20} className="text-neutral-400 dark:text-neon-200/60 shrink-0" />
    </Link>
  );
}

// ─── Referral card ─────────────────────────────────────────────────
// The user's own invite code, copy button, stats, and the how-it-works
// instruction (rendered + spoken in the user's chosen language). Moved
// here from Profile (UX Phase 2) — a referral is a reward, so it lives
// with the other ways to earn.
function ReferralCard({ user }: { user: User }) {
  const { push } = useToast();
  const { lang: uiLang } = useI18n();
  const [data, setData] = useState<{
    code: string | null;
    stats: { total: number; pending: number; credited: number; total_earned: number };
    card_image_url: string | null;
  } | null>(null);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    api.get<typeof data>('/me/referral')
      .then((d) => setData(d as NonNullable<typeof data>))
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speakLang: Lang =
    (user.language as Lang | null | undefined) ||
    (uiLang as Lang) ||
    'en';
  const intro = REFERRAL_INTROS[speakLang] ?? REFERRAL_INTROS.en;

  async function copy() {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      push('success', 'Referral code copied!');
    } catch {
      push('info', `Your code: ${data.code}`);
    }
  }

  function speak() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      push('info', 'Audio playback isn\'t supported on this device.');
      return;
    }
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(intro.text);
    u.lang = intro.bcp47;
    u.rate = 0.95;
    u.pitch = 1;
    const voices = window.speechSynthesis.getVoices();
    const exact = voices.find(v => v.lang.toLowerCase() === intro.bcp47.toLowerCase());
    const prefix = intro.bcp47.split('-')[0].toLowerCase();
    const fuzzy = voices.find(v => v.lang.toLowerCase().startsWith(prefix));
    const chosen = exact || fuzzy;
    if (chosen) u.voice = chosen;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  }

  if (!data || !data.code) return null;
  const cardIconUrl = data.card_image_url;
  return (
    <div className="card p-4 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 border-fuchsia-500/30">
      <div className="flex items-center gap-3 mb-3">
        {cardIconUrl ? (
          <img
            src={cardIconUrl}
            alt="Referral"
            className="w-12 h-12 object-contain shrink-0
                       drop-shadow-[0_2px_4px_rgba(0,0,0,0.20)]"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-fuchsia-500 text-white shadow">
            <Icon name="card_giftcard" size={22} filled />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Your referral code</div>
          <div className="font-mono font-bold text-lg tracking-wider text-neutral-900 dark:text-neutral-100">
            {data.code}
          </div>
        </div>
        <button
          type="button"
          onClick={speak}
          aria-label={speaking ? 'Stop reading referral instructions' : 'Listen to referral instructions'}
          aria-pressed={speaking}
          className={`shrink-0 w-11 h-11 rounded-full grid place-items-center transition active:scale-[.94] ${
            speaking
              ? 'bg-brand-500 text-white shadow-md animate-pulse'
              : 'bg-brand-500/15 text-brand-600 dark:text-brand-400 hover:bg-brand-500/25'
          }`}
        >
          <Icon name={speaking ? 'stop_circle' : 'volume_up'} size={20} filled />
        </button>
        <button onClick={copy} className="btn-ghost text-xs px-3 min-h-[44px] inline-flex items-center gap-1">
          <Icon name="content_copy" size={16} /> Copy
        </button>
      </div>
      <p
        lang={intro.bcp47}
        className="text-xs text-neutral-600 dark:text-neutral-400 mb-3 leading-relaxed"
      >
        {intro.text}
      </p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-neutral-100 dark:bg-neutral-900 p-2">
          <div className="text-lg font-bold">{data.stats.total}</div>
          <div className="text-[10px] text-neutral-500 uppercase">Total</div>
        </div>
        <div className="rounded-lg bg-neutral-100 dark:bg-neutral-900 p-2">
          <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{data.stats.pending}</div>
          <div className="text-[10px] text-neutral-500 uppercase">Pending</div>
        </div>
        <div className="rounded-lg bg-neutral-100 dark:bg-neutral-900 p-2">
          <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
           {data.stats.total_earned.toLocaleString('en-IN')}
          </div>
          <div className="text-[10px] text-neutral-500 uppercase">Earned</div>
        </div>
      </div>
    </div>
  );
}
